import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { customAltApi } from '@/api/customAltApi';

interface CustomAltEditorProps {
  imageId: number;
  customAltText: string | null;
  onSaved: (customAltText: string) => void;
  onDeleted: () => void;
}

export function CustomAltEditor({ imageId, customAltText, onSaved, onDeleted }: CustomAltEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(customAltText || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(customAltText || '');
  }, [customAltText]);

  const validate = (text: string): string | null => {
    if (text.length > 500) {
      return '최대 500자까지 입력 가능합니다.';
    }
    if (/<[^>]*>/.test(text)) {
      return 'HTML 태그는 사용할 수 없습니다.';
    }
    // 비속어 필터링은 백엔드에서 처리
    return null;
  };

  const handleSave = async () => {
    const validationError = validate(value);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await customAltApi.saveCustomAlt(imageId, value);
      if (res.data.success) {
        onSaved(value);
        setIsEditing(false);
      } else {
        setError(res.data.message || '저장에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!customAltText) return;
    
    setLoading(true);
    setError(null);
    try {
      const res = await customAltApi.deleteCustomAlt(imageId);
      if (res.data.success) {
        onDeleted();
        setIsEditing(false);
      } else {
        setError(res.data.message || '삭제에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || '삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setValue(customAltText || '');
    setError(null);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="커스텀 ALT 텍스트 입력 (최대 500자)"
            disabled={loading}
            maxLength={500}
          />
          <Button size="sm" onClick={handleSave} disabled={loading}>
            {loading ? <Save className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel} disabled={loading}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {customAltText ? (
        <>
          <span className="text-sm font-medium text-blue-600">{customAltText}</span>
          <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDelete} disabled={loading}>
            {loading ? <Save className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </Button>
        </>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
          <Edit2 className="w-4 h-4 mr-1" />
          커스텀 ALT 추가
        </Button>
      )}
    </div>
  );
}
